import { useAnalytics } from '@app/analytics/use-analytics';
import { getOrganizationId, getUserExternalId } from '@app/store/selectors';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import type { NavigateProps } from 'react-router-dom';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { WIZARD_ANALYTICS_EVENTS } from '../analytics/events';
import { getEventName, getOrgPaymentPlanName } from '../selectors';
import { useWizardNavigation } from './wizard';

/**
 * This is a wrapper component that is used to protect wizard routes.
 * It is used to ensure that the user starts its journey from the /experience-details step
 * and not from any other step.
 */
export const ProtectedWizardRoutes = (): JSX.Element => {
  const eventName = useSelector(getEventName);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  usePageViewTracking();

  const shouldRedirect =
    !pathname.includes('/experience-details') && !eventName;

  return shouldRedirect ? (
    <NavigateWithSearchParams to="experience-details" replace />
  ) : (
    <>
      <Helmet>
        <title>{t('create-event')} | RingCentral Events</title>
      </Helmet>
      <Outlet />
    </>
  );
};

export const NavigateWithSearchParams = ({ to, replace }: NavigateProps) => {
  const { search } = useLocation();
  return <Navigate to={to + search} replace={replace} />;
};

const usePageViewTracking = () => {
  const { track } = useAnalytics();
  const { pathname } = useLocation();
  const { steps, currentStepIndex } = useWizardNavigation();
  const userId = useSelector(getUserExternalId);
  const orgId = useSelector(getOrganizationId);
  const orgPaymentPlanName = useSelector(getOrgPaymentPlanName);

  useEffect(() => {
    track(WIZARD_ANALYTICS_EVENTS.STEP_VISITED, {
      step: steps[currentStepIndex]?.label ?? '',
      user_id: userId,
      organiser_id: orgId,
      path: location.pathname,
      organisation_subscription_tier: orgPaymentPlanName,
      referrer: document.referrer,
    });
    /**
     * We only need to re-run this effect when the pathname changes
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
};
